import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { useEffect } from 'react';
import {
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EquipmentTypePicker } from '@/components/ui/equipment-type-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ParentEquipmentPicker } from '@/components/ui/parent-equipment-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import {
  useCreateEquipment,
  useEquipmentItem,
  useUpdateEquipment,
} from '@/features/equipment/queries';
import {
  equipmentCreateSchema,
  equipmentUpdateSchema,
  type EquipmentCreateValues,
  type EquipmentUpdateValues,
} from '@/features/equipment/schema';
import {
  CRITICALITY_LEVELS,
  type Equipment,
} from '@/features/equipment/types';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Generic TextField — works with both schemas. */
function TextField<TForm extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
  hint?: string;
  type?: 'text' | 'date' | 'number';
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type={type}
              {...field}
              value={
                field.value === null || field.value === undefined
                  ? ''
                  : String(field.value)
              }
            />
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value}
      </p>
    </div>
  );
}

function CreateBranch({ defaultVesselId }: { defaultVesselId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateEquipment();

  const form = useForm<EquipmentCreateValues>({
    resolver: zodResolver(equipmentCreateSchema),
    defaultValues: {
      name: '',
      tag: '',
      serialNumber: null,
      equipmentTypeId: '',
      vesselId: defaultVesselId ?? '',
      parentEquipmentId: null,
      location: null,
      installedAt: null,
      commissionedAt: null,
      warrantyEndDate: null,
      criticality: 3,
      isActive: true,
    },
  });

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('equipment.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('equipment.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    createMut.mutate(values, {
      onSuccess: (created) => navigate(`/equipment/${created.id}`),
    });
  });

  const watchedVesselId = form.watch('vesselId');

  return (
    <FormBody
      mode="create"
      title={t('equipment.form.createTitle')}
      onSubmit={onSubmit}
      submitting={createMut.isPending}
      submitLabel={t('equipment.form.submitCreate')}
      cancelTo="/equipment"
      errorMessage={errorMessage}
      form={form}
      watchedVesselId={watchedVesselId}
    />
  );
}

function EditBranch({ existing }: { existing: Equipment }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMut = useUpdateEquipment(existing.id);

  const initial: EquipmentUpdateValues = {
    name: existing.name,
    serialNumber: existing.serialNumber,
    parentEquipmentId: existing.parentEquipmentId,
    location: existing.location,
    installedAt: existing.installedAt
      ? existing.installedAt.slice(0, 10)
      : null,
    commissionedAt: existing.commissionedAt
      ? existing.commissionedAt.slice(0, 10)
      : null,
    warrantyEndDate: existing.warrantyEndDate
      ? existing.warrantyEndDate.slice(0, 10)
      : null,
    criticality: existing.criticality,
    isActive: existing.isActive,
  };

  const form = useForm<EquipmentUpdateValues>({
    resolver: zodResolver(equipmentUpdateSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.id]);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('equipment.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('equipment.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    updateMut.mutate(values, {
      onSuccess: (updated) => navigate(`/equipment/${updated.id}`),
    });
  });

  return (
    <FormBody
      mode="edit"
      existing={existing}
      title={t('equipment.form.editTitle')}
      onSubmit={onSubmit}
      submitting={updateMut.isPending}
      submitLabel={t('equipment.form.submitUpdate')}
      cancelTo={`/equipment/${existing.id}`}
      errorMessage={errorMessage}
      form={form}
      watchedVesselId={existing.vesselId}
    />
  );
}

type FormBodyProps =
  | {
      mode: 'create';
      form: ReturnType<typeof useForm<EquipmentCreateValues>>;
      existing?: never;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      watchedVesselId: string | null;
    }
  | {
      mode: 'edit';
      form: ReturnType<typeof useForm<EquipmentUpdateValues>>;
      existing: Equipment;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      watchedVesselId: string | null;
    };

function FormBody(props: FormBodyProps) {
  const { t } = useTranslation();
  const {
    title,
    onSubmit,
    submitting,
    submitLabel,
    cancelTo,
    errorMessage,
    mode,
    form,
    watchedVesselId,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const control = form.control as Control<any>;
  const { data: types } = useEquipmentTypes();
  const { data: vessels } = useVessels();

  const existingTypeName =
    mode === 'edit'
      ? (types?.find((tp) => tp.id === props.existing.equipmentTypeId)?.name ??
        props.existing.equipmentTypeId)
      : null;
  const existingVesselName =
    mode === 'edit'
      ? (() => {
          const v = vessels?.find((x) => x.id === props.existing.vesselId);
          return v ? vesselDisplayName(v) : props.existing.vesselId;
        })()
      : null;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>

      {mode === 'edit' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>
            {t('equipment.form.immutableHint')}
          </AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('equipment.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Form {...(form as any)}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('equipment.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={control}
                name="name"
                label={t('equipment.fields.name')}
              />
              {mode === 'create' ? (
                <TextField
                  control={control}
                  name="tag"
                  label={t('equipment.fields.tag')}
                  hint={t('equipment.fields.tagHint')}
                />
              ) : (
                <ReadOnlyField
                  label={t('equipment.fields.tag')}
                  value={<span className="font-mono">{props.existing.tag}</span>}
                />
              )}
              <TextField
                control={control}
                name="serialNumber"
                label={t('equipment.fields.serialNumber')}
              />
            </CardContent>
          </Card>

          {/* Type & Location */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('equipment.detail.sectionTypeLocation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {mode === 'create' ? (
                <>
                  <FormField
                    control={control}
                    name="vesselId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('equipment.fields.vesselId')}</FormLabel>
                        <FormControl>
                          <VesselSelect
                            value={field.value || null}
                            onChange={(v) => {
                              field.onChange(v ?? '');
                              // Clear parent when vessel changes — a parent
                              // from another vessel would be invalid.
                              form.setValue('parentEquipmentId', null);
                            }}
                            allowEmpty={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="equipmentTypeId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('equipment.fields.equipmentTypeId')}
                        </FormLabel>
                        <FormControl>
                          <EquipmentTypePicker
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            allowEmpty
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyField
                    label={t('equipment.fields.vesselId')}
                    value={existingVesselName}
                  />
                  <ReadOnlyField
                    label={t('equipment.fields.equipmentTypeId')}
                    value={existingTypeName}
                  />
                </>
              )}
              <TextField
                control={control}
                name="location"
                label={t('equipment.fields.location')}
              />
            </CardContent>
          </Card>

          {/* Hierarchy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('equipment.detail.sectionHierarchy')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <FormField
                control={control}
                name="parentEquipmentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('equipment.fields.parentEquipmentId')}
                    </FormLabel>
                    <FormControl>
                      <ParentEquipmentPicker
                        vesselId={watchedVesselId}
                        value={field.value ?? null}
                        onChange={(v) => field.onChange(v)}
                        excludeId={mode === 'edit' ? props.existing.id : undefined}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t('equipment.fields.parentEquipmentHint')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Commissioning */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('equipment.detail.sectionCommissioning')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <TextField
                control={control}
                name="installedAt"
                label={t('equipment.fields.installedAt')}
                type="date"
              />
              <TextField
                control={control}
                name="commissionedAt"
                label={t('equipment.fields.commissionedAt')}
                type="date"
              />
              <TextField
                control={control}
                name="warrantyEndDate"
                label={t('equipment.fields.warrantyEndDate')}
                type="date"
              />
            </CardContent>
          </Card>

          {/* Operational */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('equipment.detail.sectionOperational')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={control}
                name="criticality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('equipment.fields.criticality')}</FormLabel>
                    <FormControl>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={field.value ?? 3}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        {CRITICALITY_LEVELS.map((lvl) => (
                          <option key={lvl} value={lvl}>
                            {t(`equipment.criticality.${lvl}`)}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0 rounded-md border p-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-primary"
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">
                      {t('equipment.fields.isActive')}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={submitting}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className={cn('animate-spin')} aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function EquipmentFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const { data: existing, isLoading } = useEquipmentItem(id);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && existing) {
    return <EditBranch existing={existing} />;
  }

  return <CreateBranch defaultVesselId={searchParams.get('vesselId')} />;
}
